import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import css from './TIORepaymentStrategy.module.scss';
import { FieldsLabels, Form } from './RepaymentStrategy';
import { CurrencyInput, FormInputWrapper, RadioGroup, TextArea } from 'form/infrastructure/reactHookForm';
import { RepaymentStrategy, RepaymentStrategyField } from 'modules/cm/RepaymentStrategy/RepaymentStrategy';
import { SectionContainer } from 'modules/cm/RepaymentStrategy/containers';
import * as sections from 'modules/cm/pages/CaseDetailsPage/subPages/RepaymentStrategy/sections';
import { CaseId } from 'modules/cm/api/cases/types';
import useYupValidationResolver from 'form/yup/reactHookFormResolver';
import validationSchema from './validationSchema';
import { Columns } from 'modules/af_cm_ui_kit';
import { Box } from 'lmc-ui-library';
import useRepaymentStrategyTotal from './useRepaymentStrategyTotal';
import { SUBPAGES } from 'modules/cm/store/subPages/types';
import CheckDirty from 'modules/cm/components/CheckDirty/CheckDirty';
import * as subPages from '../../../../store/subPages';
import { updateRepaymentStrategy } from '../../../../store/cases/update/actions';
import { saveCase } from '../../../../store/cases/actions';
import useSaveButtonClicked from '../../hooks/useSaveButtonClicked';

const YesOrNoRadio = ({ name }: { name: string }) => {
  return (
    <RadioGroup
      name={name}
      isDisplayFlex={true}
      options={[
        {
          value: 'true',
          text: 'Yes',
        },
        {
          value: 'false',
          text: 'No',
        },
      ]}
      transform={{
        input: (value) => {
          return value !== null ? value.toString() : null;
        },
        output: (value) => {
          return value !== null ? value === 'true' : null;
        },
      }}
    />
  );
};

interface Props {
  caseId: CaseId;
  initialRepaymentStrategy: RepaymentStrategy;
  repaymentStrategy: RepaymentStrategy;
}

const RepaymentStrategyForm: React.FC<Props> = ({ caseId, initialRepaymentStrategy, repaymentStrategy }) => {
  const { addListener, removeListener } = useSaveButtonClicked();

  const methods = useForm<Form>({
    mode: 'all',
    resolver: useYupValidationResolver(validationSchema()),
    defaultValues: initialRepaymentStrategy
      ? initialRepaymentStrategy
      : {
          [RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing]: null,
          [RepaymentStrategyField.HaveSaleOfAnotherProperty]: null,
          [RepaymentStrategyField.HaveInvestments]: null,
          [RepaymentStrategyField.HaveEndownments]: null,
          [RepaymentStrategyField.HaveOther]: null,

          [RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing]: null,
          [RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty]: null,
          [RepaymentStrategyField.RepaymentValueOfHaveInvestments]: null,
          [RepaymentStrategyField.RepaymentValueOfHaveEndownments]: null,
          [RepaymentStrategyField.RepaymentValueOfHaveOther]: null,
          [RepaymentStrategyField.RepaymentValueOfHaveOther]: null,
          [RepaymentStrategyField.DetailsForOther]: null,

          [RepaymentStrategyField.TotalAmountToBeRepaid]: null,
          [RepaymentStrategyField.RepaymentValueTotalCalculated]: null,

          properties: [],
          investments: [],
          endowments: [],
        },
  });

  const { reset } = methods;

  useEffect(() => {
    if (repaymentStrategy) {
      reset(repaymentStrategy, {
        keepDefaultValues: true,
      });
    }
  }, [repaymentStrategy, reset]);

  const dispatch = useDispatch();

  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateRepaymentStrategy(methods.getValues()));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateRepaymentStrategy(methods.getValues()));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [
    haveSaleOfPropertyAndDownsizing,
    haveSaleOfAnotherProperty,
    haveEndownments,
    haveInvestments,
    haveOther,
  ] = methods.watch([
    RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing,
    RepaymentStrategyField.HaveSaleOfAnotherProperty,
    RepaymentStrategyField.HaveEndownments,
    RepaymentStrategyField.HaveInvestments,
    RepaymentStrategyField.HaveOther,
  ]);

  const [
    repaymentValueOfHaveEndownments,
    repaymentValueOfHaveInvestments,
    repaymentValueOfHaveOther,
    repaymentValueOfHaveSaleOfAnotherProperty,
    repaymentValueOfSaleOfPropertyAndDownsizing,
  ] = methods.watch([
    RepaymentStrategyField.RepaymentValueOfHaveEndownments,
    RepaymentStrategyField.RepaymentValueOfHaveInvestments,
    RepaymentStrategyField.RepaymentValueOfHaveOther,
    RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty,
    RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing,
  ]);

  const repaymentStrategyTotal = useRepaymentStrategyTotal([
    [haveSaleOfPropertyAndDownsizing, repaymentValueOfSaleOfPropertyAndDownsizing],
    [haveSaleOfAnotherProperty, repaymentValueOfHaveSaleOfAnotherProperty],
    [haveEndownments, repaymentValueOfHaveEndownments],
    [haveInvestments, repaymentValueOfHaveInvestments],
    [haveOther, repaymentValueOfHaveOther],
  ]);

  const totalAmountToBeRepaid = methods.watch(RepaymentStrategyField.TotalAmountToBeRepaid);

  useEffect(() => {
    if (Number(totalAmountToBeRepaid) > Number(repaymentStrategyTotal)) {
      methods.setError(RepaymentStrategyField.RepaymentValueTotalCalculated, {
        type: 'Calculated total repayment matches the Total to repay',
        message:
          'The total repayment strategy provided is lower than the total amount to be repaid. This must be corrected to proceed with the case',
      });
    } else {
      methods.clearErrors(RepaymentStrategyField.RepaymentValueTotalCalculated);
    }
  }, [methods, totalAmountToBeRepaid, repaymentStrategyTotal]);

  useEffect(() => {
    methods.setValue(RepaymentStrategyField.RepaymentValueTotalCalculated, repaymentStrategyTotal);
  }, [methods, repaymentStrategyTotal]);

  return (
    <FormProvider<Form> {...methods}>
      <CheckDirty
        initialData={methods.getValues()}
        onDirtyChange={(isDirty) => {
          dispatch(subPages.actions.updateSubPageIsDirty(SUBPAGES.REPAYMENT_STRATEGY, isDirty));
        }}
      />
      <form className={css.form}>
        <SectionContainer field={RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing}>
          <h2>Sale of property/downsizing</h2>
          <FormInputWrapper
            title={FieldsLabels.HaveSaleOfPropertyAndDownsizing}
            name={RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing}>
            <YesOrNoRadio name={RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing} />
          </FormInputWrapper>

          {haveSaleOfPropertyAndDownsizing && (
            <FormInputWrapper
              title={FieldsLabels.HowMuchOfTheLoanWillBeRepaid}
              name={RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing}>
              <CurrencyInput />
            </FormInputWrapper>
          )}
        </SectionContainer>

        <SectionContainer field={RepaymentStrategyField.HaveSaleOfAnotherProperty}>
          <h2>Sale of another property</h2>
          <FormInputWrapper
            title={FieldsLabels.HaveSaleOfAnotherProperty}
            name={RepaymentStrategyField.HaveSaleOfAnotherProperty}>
            <YesOrNoRadio name={RepaymentStrategyField.HaveSaleOfAnotherProperty} />
          </FormInputWrapper>

          {haveSaleOfAnotherProperty && (
            <>
              <sections.Property
                caseId={caseId}
                control={methods.control}
                reset={() => methods.reset(methods.getValues())}
              />
              <FormInputWrapper
                title={FieldsLabels.HowMuchOfTheLoanWillBeRepaid}
                name={RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty}>
                <CurrencyInput />
              </FormInputWrapper>
            </>
          )}
        </SectionContainer>

        <SectionContainer field={RepaymentStrategyField.HaveInvestments}>
          <h2>Investments</h2>
          <FormInputWrapper title={FieldsLabels.HaveInvestments} name={RepaymentStrategyField.HaveInvestments}>
            <YesOrNoRadio name={RepaymentStrategyField.HaveInvestments} />
          </FormInputWrapper>

          {haveInvestments && (
            <>
              <sections.Investment
                caseId={caseId}
                control={methods.control}
                reset={() => methods.reset(methods.getValues())}
              />
              <FormInputWrapper
                title={FieldsLabels.HowMuchOfTheLoanWillBeRepaid}
                name={RepaymentStrategyField.RepaymentValueOfHaveInvestments}>
                <CurrencyInput />
              </FormInputWrapper>
            </>
          )}
        </SectionContainer>

        <SectionContainer field={RepaymentStrategyField.HaveEndownments}>
          <h2>Endowments</h2>
          <FormInputWrapper title={FieldsLabels.HaveEndownments} name={RepaymentStrategyField.HaveEndownments}>
            <YesOrNoRadio name={RepaymentStrategyField.HaveEndownments} />
          </FormInputWrapper>

          {haveEndownments && (
            <>
              <sections.Endowment
                caseId={caseId}
                control={methods.control}
                reset={() => methods.reset(methods.getValues())}
              />
              <FormInputWrapper
                title={FieldsLabels.HowMuchOfTheLoanWillBeRepaid}
                name={RepaymentStrategyField.RepaymentValueOfHaveEndownments}>
                <CurrencyInput />
              </FormInputWrapper>
            </>
          )}
        </SectionContainer>

        <SectionContainer field={RepaymentStrategyField.HaveOther}>
          <h2>Other</h2>
          <FormInputWrapper title={FieldsLabels.HaveOther} name={RepaymentStrategyField.HaveOther}>
            <YesOrNoRadio name={RepaymentStrategyField.HaveOther} />
          </FormInputWrapper>

          {haveOther && (
            <>
              <FormInputWrapper title={FieldsLabels.AddDetailsForOther} name={RepaymentStrategyField.DetailsForOther}>
                <TextArea />
              </FormInputWrapper>

              <FormInputWrapper
                title={FieldsLabels.HowMuchOfTheLoanWillBeRepaid}
                name={RepaymentStrategyField.RepaymentValueOfHaveOther}>
                <CurrencyInput />
              </FormInputWrapper>
            </>
          )}
        </SectionContainer>

        <Box>
          <Box.Content style={{ padding: '0 12px', margin: '-12px 0' }}>
            <Columns spacing={4}>
              <FormInputWrapper
                title={FieldsLabels.TotalAmountToBeRepaid}
                name={RepaymentStrategyField.TotalAmountToBeRepaid}>
                <CurrencyInput readOnly />
              </FormInputWrapper>
              <FormInputWrapper
                title={FieldsLabels.RepaymentValueTotalCalculated}
                name={RepaymentStrategyField.RepaymentValueTotalCalculated}>
                <CurrencyInput readOnly />
              </FormInputWrapper>
            </Columns>
          </Box.Content>
        </Box>
      </form>
    </FormProvider>
  );
};

export default RepaymentStrategyForm;
